<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">{{ $t('Projects') }}</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('projects')"
                                :key="badge.key"
                                :title="badge.title || 'Shortcut'"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <router-link v-if="isAdminRole" aria-label="Projekt anlegen" class="btn-stage-icon-plain" title="Projekt anlegen" to="/workspace/projects/new">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
                    </router-link>
                    <button
                        id="filter-button"
                        :aria-label="$t('Filter menu')"
                        class="btn-stage-icon-plain stage-filter-icon-button"
                        :title="$t('Filter menu')"
                        type="button"
                        @click="toggleFiltersSidebar"
                    >
                        <span
                            v-if="hasActiveFilters"
                            class="stage-filter-active-dot"
                            aria-hidden="true"
                        ></span>
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                    </button>
                    <button
                        :aria-label="$t('Reload')"
                        type="button"
                        class="btn-stage-icon-plain"
                        :title="$t('Reload')"
                        @click="getProjects"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>
                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div class="h-full overflow-auto sm:overflow-hidden">
                        <template v-if="projectList.length > 0">
                            <div class="sm:hidden">
                                <ul class="border-b border-gray-200 divide-y divide-gray-border">
                                    <li
                                        v-for="project in projectList"
                                        :key="'project-mobile-' + project.id"
                                        :class="isAdminRole ? 'cursor-pointer' : ''"
                                        class="px-4 py-4 sm:px-6"
                                        @click="onProjectRowClick(project)"
                                    >
                                        <div class="flex items-center space-x-3">
                                            <img
                                                :src="projectImageUrl(project)"
                                                :alt="project.title"
                                                class="h-9 w-9 rounded-sm object-cover"
                                            >
                                            <div class="min-w-0 flex-1">
                                                <div class="text-sm font-medium text-gray-900 truncate">{{ project.title }}</div>
                                                <div class="text-sm text-gray-500 truncate">{{ project.description || '-' }}</div>
                                                <div class="mt-1 flex items-center gap-3 text-xs">
                                                    <router-link
                                                        v-if="projectTicketsCount(project) > 0"
                                                        :to="projectTicketsRoute(project)"
                                                        class="inline-flex items-center text-sm leading-5 text-gray-700 hover:text-gray-700 transition-colors duration-150"
                                                        @click.stop
                                                    >
                                                        <span>{{ projectTicketsCount(project) }} anzeigen</span>
                                                    </router-link>
                                                    <router-link
                                                        v-if="projectPositionsCount(project) > 0"
                                                        :to="projectPositionsRoute(project)"
                                                        class="inline-flex items-center text-sm leading-5 text-gray-700 hover:text-gray-700 transition-colors duration-150"
                                                        @click.stop
                                                    >
                                                        <span>{{ projectPositionsCount(project) }} anzeigen</span>
                                                    </router-link>
                                                </div>
                                                <div class="mt-2 text-xs text-gray-500">{{ $momentFormatDateTimeAgo(project.updated_at) }}</div>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <div class="hidden sm:flex stage-listview-split stage-listview-split-keep-last-border">
                                <div class="stage-listview-split-header">
                                    <table class="stage-listview-split-table table-listview project-list-split-table">
                                        <colgroup>
                                            <col class="project-list-col-project">
                                            <col class="project-list-col-description">
                                            <col class="w-32">
                                            <col class="w-32">
                                            <col class="w-40">
                                            <col class="w-44">
                                        </colgroup>
                                        <thead>
                                        <tr>
                                            <th class="table-listview-l-th project-list-col-project">PROJEKT</th>
                                            <th class="table-listview-l-th project-list-col-description">BESCHREIBUNG</th>
                                            <th class="table-listview-l-th whitespace-nowrap w-32">TICKETS</th>
                                            <th class="table-listview-l-th whitespace-nowrap w-32">POSITIONEN</th>
                                            <th class="table-listview-l-th whitespace-nowrap w-40">ANWENDER</th>
                                            <th class="table-listview-l-th whitespace-nowrap w-44">UPDATE</th>
                                        </tr>
                                        </thead>
                                    </table>
                                </div>
                                <div class="stage-listview-split-body">
                                    <table class="stage-listview-split-table table-listview table-listview--zebra project-list-split-table">
                                        <colgroup>
                                            <col class="project-list-col-project">
                                            <col class="project-list-col-description">
                                            <col class="w-32">
                                            <col class="w-32">
                                            <col class="w-40">
                                            <col class="w-44">
                                        </colgroup>
                                        <tbody class="bg-white">
                                        <tr
                                            v-for="project in projectList"
                                            :key="'project-row-' + project.id"
                                            :class="isAdminRole ? 'cursor-pointer' : ''"
                                            @click="onProjectRowClick(project)"
                                        >
                                            <td class="table-listview-l-td whitespace-no-wrap whitespace-nowrap project-list-col-project">
                                                <div class="flex items-center min-w-0">
                                                    <img
                                                        :src="projectImageUrl(project)"
                                                        :alt="project.title"
                                                        class="h-9 w-9 rounded-sm object-cover mr-3 flex-shrink-0"
                                                    >
                                                    <div class="min-w-0 w-full">
                                                        <div class="text-sm font-medium text-gray-700 truncate w-full">{{ project.title }}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td class="table-listview-l-td whitespace-no-wrap whitespace-nowrap project-list-col-description">
                                                <div class="text-sm text-gray-500 truncate w-full">{{ project.description || '-' }}</div>
                                            </td>
                                            <td class="table-listview-l-td whitespace-no-wrap whitespace-nowrap w-32">
                                                <router-link
                                                    v-if="projectTicketsCount(project) > 0"
                                                    :to="projectTicketsRoute(project)"
                                                    class="inline-flex items-center text-sm leading-5 text-gray-700 hover:text-gray-700 transition-colors duration-150"
                                                    @click.stop
                                                >
                                                    <span>{{ projectTicketsCount(project) }} anzeigen</span>
                                                </router-link>
                                                <span v-else class="text-sm text-gray-500">-</span>
                                            </td>
                                            <td class="table-listview-l-td whitespace-no-wrap whitespace-nowrap w-32">
                                                <router-link
                                                    v-if="projectPositionsCount(project) > 0"
                                                    :to="projectPositionsRoute(project)"
                                                    class="inline-flex items-center text-sm leading-5 text-gray-700 hover:text-gray-700 transition-colors duration-150"
                                                    @click.stop
                                                >
                                                    <span>{{ projectPositionsCount(project) }} anzeigen</span>
                                                </router-link>
                                                <span v-else class="text-sm text-gray-500">-</span>
                                            </td>
                                            <td class="table-listview-l-td whitespace-no-wrap whitespace-nowrap w-40">
                                                <div class="flex items-center">
                                                    <img
                                                        v-for="(user, index) in projectVisibleAssignedUsers(project)"
                                                        :key="'project-assigned-user-' + project.id + '-' + user.id + '-' + index"
                                                        :src="projectAssignedUserAvatar(user)"
                                                        :alt="user.name || 'Anwender'"
                                                        :title="user.name || ''"
                                                        :class="index === 0 ? '' : '-ml-2'"
                                                        class="inline-block h-6 w-6 rounded-full border border-white object-cover"
                                                    >
                                                    <span
                                                        v-if="projectRemainingAssignedUsersCount(project) > 0"
                                                        :title="projectRemainingAssignedUsersCount(project) + ' weitere Anwender'"
                                                        :class="projectVisibleAssignedUsers(project).length > 0 ? '-ml-2' : ''"
                                                        class="inline-flex h-6 w-6 items-center justify-center rounded-full border border-white bg-gray-200 text-xs font-semibold text-gray-500"
                                                    >
                                                        +
                                                    </span>
                                                    <span v-if="projectAssignedUsers(project).length < 1" class="text-sm leading-5 text-gray-500">
                                                        -
                                                    </span>
                                                </div>
                                            </td>
                                            <td class="table-listview-l-td whitespace-no-wrap whitespace-nowrap w-44">
                                                <div class="flex items-center text-sm text-gray-500 truncate w-40">
                                                    <img
                                                        :src="updateUserAvatar(project)"
                                                        :alt="updateUserName(project)"
                                                        class="h-5 w-5 rounded-full object-cover mr-2 flex-shrink-0"
                                                    >
                                                    <span class="truncate">{{ $momentFormatDateTimeAgo(project.updated_at) }}</span>
                                                </div>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </template>

                        <template v-else-if="!loading">
                            <empty-state />
                        </template>
                    </div>

                    <aside
                        :aria-hidden="!filtersSidebar"
                        class="stage-filter-sidebar stage-filter-panel w-80 overflow-auto z-20"
                        :class="filtersSidebar ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                    >
                        <div class="stage-filter-body">
                            <div class="stage-filter-title-row">
                                <svg-vue class="stage-filter-title-icon h-5 w-5 text-gray-600 mr-3" icon="font-awesome.regular.sliders"></svg-vue>
                                <div class="stage-filter-title">{{ $t('Filter & Sorting') }}</div>
                            </div>
                            <div class="stage-filter-label">{{ $t('Sort by') }}</div>
                            <div class="stage-filter-row mb-3">
                                <button class="stage-filter-sort-btn text-sm leading-5 font-medium" type="button" @click="changeSortOrder">
                                    <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-down-arrow-up"/>
                                </button>
                                <div class="stage-filter-sort-select-wrap">
                                    <select
                                        v-model="sort.column"
                                        aria-label="Sort by"
                                        class="stage-filter-sort-select block form-select text-sm leading-5 font-medium"
                                        @change="getProjects"
                                    >
                                        <option value="title">Titel</option>
                                        <option value="created_at">Erstellt am</option>
                                        <option value="updated_at">Zuletzt aktualisiert</option>
                                    </select>
                                    <span class="stage-filter-select-icon">
                                        <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                    </span>
                                </div>
                            </div>
                            <div class="stage-filter-label">{{ $t('Search') }}</div>
                            <div class="mb-4">
                                <div class="relative">
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                    </div>
                                    <input
                                        v-model.lazy="filters.search"
                                        :placeholder="$t('Search')"
                                        class="stage-filter-input form-input block pl-10 text-sm"
                                        @change="getProjects"
                                    >
                                </div>
                            </div>
                            <template v-if="canUseRelationshipFilter">
                                <div class="stage-filter-label">Teilnehmer</div>
                                <div class="mb-4">
                                    <input-select
                                        id="project-relationship-participant"
                                        v-model="filters.relationship_user_id"
                                        :options="participantList"
                                        class="stage-filter-select"
                                        option-label="name"
                                        searchable
                                        @change="getProjects"
                                    >
                                        <template v-slot:selectedOption="props">
                                            <template v-if="!props || !props.anySelected || !props.option">
                                                <span class="block truncate">Alle Teilnehmer</span>
                                            </template>
                                            <template v-else>
                                                <div class="flex min-w-0 items-center space-x-3">
                                                    <img
                                                        :alt="props.option.name || 'Teilnehmer'"
                                                        :src="participantAvatar(props.option)"
                                                        class="h-5 w-5 flex-shrink-0 rounded-full object-cover"
                                                    >
                                                    <span class="block truncate">{{ props.option.name }}</span>
                                                </div>
                                            </template>
                                        </template>
                                        <template v-slot:selectOption="props">
                                            <div class="flex min-w-0 items-center space-x-3">
                                                <img
                                                    :alt="props.option.name || 'Teilnehmer'"
                                                    :src="participantAvatar(props.option)"
                                                    class="h-5 w-5 flex-shrink-0 rounded-full object-cover"
                                                >
                                                <span
                                                    :class="Number(props.option.id) === Number(filters.relationship_user_id) ? 'font-semibold' : 'font-normal'"
                                                    class="block truncate"
                                                >
                                                    {{ props.option.name }}
                                                </span>
                                            </div>
                                        </template>
                                    </input-select>
                                </div>
                            </template>
                            <div v-if="anyFilter">
                                <button
                                    type="button"
                                    class="text-sm text-gray-500 hover:text-gray-600 focus:outline-none"
                                    @click="resetFilters"
                                >
                                    {{ $t('Reset selection') }}
                                </button>
                            </div>
                        </div>
                    </aside>
                </div>

                <list-footer-pagination
                    :start="paginationStart"
                    :end="paginationEnd"
                    :total="pagination.total"
                    :current-page="pagination.currentPage"
                    :total-pages="pagination.totalPages"
                    nav-class="stage-list-footer-no-top-border"
                    @prev="changePage(pagination.currentPage - 1)"
                    @next="changePage(pagination.currentPage + 1)"
                />
            </div>
        </div>
    </div>
</template>

<script>
import {resolveStoreAccess} from "@/utils/access-context";
import {
    queryNumber,
    queryString,
    queryWithoutKeys,
    routeWithReturnTo,
    sameRouteQuery,
    setQueryValue,
} from "@/utils/list-route-state";

export default {
    name: 'dashboard-projects-list',
    metaInfo() {
        return {
            title: this.$t('Projects').toString(),
        };
    },
    data() {
        const routeQuery = this.$route && this.$route.query ? this.$route.query : {};
        const allowedSortColumns = ['title', 'created_at', 'updated_at'];
        const initialSortColumn = queryString(routeQuery, 'list_sort_column', 'title');
        const initialSortOrder = queryString(routeQuery, 'list_sort_order', 'asc') === 'desc' ? 'desc' : 'asc';
        const initialRelationshipUserId = queryNumber(routeQuery, 'list_relationship_user_id', null);

        return {
            page: queryNumber(routeQuery, 'list_page', 1),
            perPage: queryNumber(routeQuery, 'list_per_page', 25),
            loading: true,
            filtersSidebar: false,
            filters: {
                search: queryString(routeQuery, 'list_search', ''),
                relationship_user_id: initialRelationshipUserId !== null && initialRelationshipUserId > 0
                    ? initialRelationshipUserId
                    : null,
            },
            sort: {
                order: initialSortOrder,
                column: allowedSortColumns.includes(initialSortColumn) ? initialSortColumn : 'title',
            },
            pagination: {
                currentPage: 1,
                perPage: 25,
                total: 0,
                totalPages: 1,
            },
            participantList: [],
            selectedParticipant: null,
            projectList: [],
        };
    },
    mounted() {
        if (!this.canUseRelationshipFilter) {
            this.filters.relationship_user_id = null;
        }
        this.getProjects();
        if (this.canUseRelationshipFilter) {
            this.getParticipants();
        }
    },
    filters: {
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    computed: {
        isAdminRole() {
            return resolveStoreAccess(this.$store).is_admin;
        },
        canUseRelationshipFilter() {
            return !!resolveStoreAccess(this.$store).modules.relationships;
        },
        anyFilter() {
            return this.hasActiveFilters;
        },
        hasActiveFilters() {
            return (this.filters.search || '').trim() !== ''
                || Number(this.filters.relationship_user_id || 0) > 0
                || this.sort.column !== 'title'
                || this.sort.order !== 'asc'
                || Number(this.perPage || 0) !== 25;
        },
        paginationStart() {
            if (this.pagination.total < 1) {
                return 0;
            }

            return ((this.pagination.currentPage - 1) * this.pagination.perPage) + 1;
        },
        paginationEnd() {
            if (this.pagination.total < 1) {
                return 0;
            }

            const pageEnd = this.pagination.currentPage * this.pagination.perPage;
            return pageEnd > this.pagination.total ? this.pagination.total : pageEnd;
        },
    },
    methods: {
        toggleFiltersSidebar() {
            this.filtersSidebar = !this.filtersSidebar;
        },
        changePage(page) {
            if ((page > 0) && (page <= this.pagination.totalPages) && (page !== this.page)) {
                this.page = page;
                this.getProjects();
            }
        },
        changeSortOrder() {
            this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc';
            this.getProjects();
        },
        resetFilters() {
            this.filters.search = '';
            this.filters.relationship_user_id = null;
            this.sort = {
                order: 'asc',
                column: 'title',
            };
            this.page = 1;
            this.getProjects();
        },
        getProjects() {
            const self = this;
            self.syncProjectsListRouteState();
            self.loading = true;
            axios.get('api/workspace/projects', {
                params: {
                    page: self.page,
                    sort: self.sort,
                    search: self.filters.search,
                    relationship_user_id: Number(self.filters.relationship_user_id || 0) > 0
                        ? Number(self.filters.relationship_user_id)
                        : null,
                    perPage: self.perPage,
                },
            }).then(function (response) {
                self.projectList = Array.isArray(response.data.items) ? response.data.items : [];
                self.pagination = response.data.pagination || self.pagination;
                const selectedParticipant = response.data.selected_relationship_user;
                self.selectedParticipant = selectedParticipant && Number(selectedParticipant.id) > 0
                    ? {
                        id: Number(selectedParticipant.id),
                        name: selectedParticipant.name || `#${selectedParticipant.id}`,
                        avatar: selectedParticipant.avatar || null,
                    }
                    : null;
                self.mergeSelectedParticipant();

                if (self.pagination.totalPages > 0 && self.page > self.pagination.totalPages) {
                    self.page = self.pagination.totalPages;
                    self.getProjects();
                    return;
                }

                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        syncProjectsListRouteState() {
            if (!this.$router || !this.$route) {
                return;
            }

            const removeKeys = [
                'list_page',
                'list_per_page',
                'list_sort_column',
                'list_sort_order',
                'list_search',
                'list_relationship_user_id',
            ];

            let nextQuery = queryWithoutKeys(this.$route.query || {}, removeKeys);
            setQueryValue(nextQuery, 'list_page', Number(this.page || 1) === 1 ? null : Number(this.page || 1));
            setQueryValue(nextQuery, 'list_per_page', Number(this.perPage || 0) === 25 ? null : Number(this.perPage || 0));
            setQueryValue(nextQuery, 'list_sort_column', this.sort.column === 'title' ? null : this.sort.column);
            setQueryValue(nextQuery, 'list_sort_order', this.sort.order === 'asc' ? null : this.sort.order);
            setQueryValue(nextQuery, 'list_search', (this.filters.search || '').trim() || null);
            setQueryValue(
                nextQuery,
                'list_relationship_user_id',
                Number(this.filters.relationship_user_id || 0) > 0
                    ? Number(this.filters.relationship_user_id)
                    : null
            );

            if (!sameRouteQuery(this.$route.query || {}, nextQuery)) {
                this.$router.replace({path: this.$route.path, query: nextQuery}).catch(() => {});
            }
        },
        getParticipants() {
            if (!this.canUseRelationshipFilter) {
                this.participantList = [];
                return;
            }

            axios.get('api/workspace/relationships', {
                params: {
                    page: 1,
                    perPage: 50,
                    sort_column: 'name',
                    sort_order: 'asc',
                },
            }).then((response) => {
                const items = Array.isArray(response?.data?.items) ? response.data.items : [];
                this.participantList = items
                    .filter((item) => item && item.type === 'user' && Number(item.entity_id) > 0)
                    .map((item) => ({
                        id: Number(item.entity_id),
                        name: item.name || `#${item.entity_id}`,
                        avatar: item.avatar || null,
                    }));
                this.mergeSelectedParticipant();
            }).catch(() => {
                this.participantList = [];
                this.mergeSelectedParticipant();
            });
        },
        mergeSelectedParticipant() {
            const selectedId = Number(this.filters.relationship_user_id || 0);
            if (
                selectedId < 1
                || !this.selectedParticipant
                || Number(this.selectedParticipant.id) !== selectedId
                || this.participantList.some((participant) => Number(participant.id) === selectedId)
            ) {
                return;
            }

            this.participantList = [
                this.selectedParticipant,
                ...this.participantList,
            ];
        },
        participantAvatar(participant) {
            return participant && participant.avatar
                ? participant.avatar
                : '/images/default/icon.png';
        },
        projectImageUrl(project) {
            if (project && project.image_url) {
                return project.image_url;
            }

            return this.generateFallbackImage(project);
        },
        userAvatar(user) {
            if (user && user.avatar) {
                return user.avatar;
            }
            if (user && user.gravatar) {
                return user.gravatar;
            }

            return '/images/default/icon.png';
        },
        updateUser(project) {
            return (project && project.created_by_user) ? project.created_by_user : null;
        },
        updateUserAvatar(project) {
            return this.userAvatar(this.updateUser(project));
        },
        updateUserName(project) {
            const user = this.updateUser(project);
            return user && user.name ? user.name : 'Profilbild';
        },
        onProjectRowClick(project) {
            if (!this.isAdminRole) {
                return;
            }

            this.navigateToEdit(project);
        },
        navigateToEdit(project) {
            if (!project || !project.id) {
                return;
            }

            this.$router.push(routeWithReturnTo('/workspace/projects/' + project.id + '/edit', this.$route, '/workspace/projects'));
        },
        generateFallbackImage(project) {
            const seedText = String(project && project.title ? project.title : 'P').trim() || 'P';
            const firstChar = seedText.charAt(0).toUpperCase();
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80" width="80" height="80"><defs><linearGradient id="projectGradientListDashboard" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="hsl(210 35% 48%)"/><stop offset="100%" stop-color="hsl(210 35% 34%)"/></linearGradient></defs><rect width="80" height="80" fill="url(#projectGradientListDashboard)"/><text x="50%" y="54%" text-anchor="middle" dominant-baseline="middle" fill="rgba(255,255,255,0.55)" font-family="Arial, sans-serif" font-size="34" font-weight="700">${firstChar}</text></svg>`;
            return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
        },
        projectAssignedUsers(project) {
            if (!project || !Array.isArray(project.assigned_users)) {
                return [];
            }

            return project.assigned_users.filter((user) => user && Number(user.id) > 0);
        },
        projectVisibleAssignedUsers(project) {
            return this.projectAssignedUsers(project).slice(0, 5);
        },
        projectRemainingAssignedUsersCount(project) {
            return Math.max(this.projectAssignedUsers(project).length - 5, 0);
        },
        projectAssignedUserAvatar(user) {
            if (user && user.avatar && user.avatar !== 'gravatar') {
                return user.avatar;
            }

            if (user && user.gravatar) {
                return user.gravatar;
            }

            return '/images/default/icon.png';
        },
        projectTicketsCount(project) {
            return Number(project && project.tickets_count ? project.tickets_count : 0);
        },
        projectPositionsCount(project) {
            return Number(project && project.positions_count ? project.positions_count : 0);
        },
        projectTicketsRoute(project) {
            const projectId = Number(project && project.id ? project.id : 0);
            const isWorkspacePath = String(this.$route && this.$route.path ? this.$route.path : '').startsWith('/workspace');
            return {
                path: isWorkspacePath ? '/workspace/tickets' : '/tickets/list',
                query: {
                    projects: projectId > 0 ? [projectId] : [],
                },
            };
        },
        projectPositionsRoute(project) {
            const projectId = Number(project && project.id ? project.id : 0);
            const isWorkspacePath = String(this.$route && this.$route.path ? this.$route.path : '').startsWith('/workspace');
            return {
                path: isWorkspacePath ? '/workspace/positions' : '/positions/list',
                query: {
                    projects: projectId > 0 ? [projectId] : [],
                },
            };
        },
    },
};
</script>

<style lang="scss">
.project-list-split-table {
    table-layout: fixed;
}

.project-list-col-project {
    min-width: 300px;
    width: 320px;
}

.project-list-col-description {
    width: calc(100% - 57rem);
}
</style>
